import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { RestaurantLogo } from "@/components/restaurant/RestaurantLogo";
import { RestaurantSocialLinks } from "@/components/restaurant/RestaurantSocialLinks";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

import { PullToRefreshIndicator } from "@/components/PullToRefreshIndicator";
import { toast } from "sonner";
import { usePWA } from "@/hooks/usePWA";
import { useMenuCache } from "@/hooks/useMenuCache";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { usePWAAnalytics } from "@/hooks/usePWAAnalytics";
import { 
  ArrowLeft, 
  Phone, 
  MapPin, 
  Clock,
  MessageCircle,
  Leaf, 
  Wheat, 
  Droplets, 
  Shield, 
  Dumbbell,
  Zap,
  ChefHat,
  ChevronDownCircle,
  ChevronDown,
  Plus,
  ChevronRight,
  Sparkles,
  Rocket,
  HelpCircle,
  PauseCircle,
  UtensilsCrossed,
  Flame,
  Search,
  X,
  ListTree
} from "lucide-react";
import PromotionCountdown from "@/components/PromotionCountdown";
import { AddToOrderButton } from "@/components/ordering/AddToOrderButton";
import { OrderCartSheet } from "@/components/ordering/OrderCartSheet";
import { useMenuCart } from "@/hooks/useMenuCart";
import { cartHasUnknownPrices } from "@/lib/ordering/cart";
import { DEFAULT_ORDERING_SETTINGS, enabledModes, isOrderingActive, parseOrderingSettings } from "@/lib/ordering/settings";
import { normalizeWhatsappNumber } from "@/lib/ordering/whatsapp";
import { trackOrderingEvent, trackOrderingEventOnce } from "@/lib/ordering/analytics";
import { buildRestaurantSeo } from "@/lib/seo/restaurant";
import { applyStaticHeadOverride } from "@/lib/seo/staticHead";
import { ShoppingBag } from "lucide-react";

interface Restaurant {
  id: string;
  name: string;
  logo_url?: string;
  brand_primary?: string;
  brand_accent?: string;
  menu_slug: string;
  slug?: string;
  show_plaxter_branding?: boolean;
  whatsapp?: string;
  instagram?: string;
  facebook?: string;
}

interface RestaurantStatus {
  is_open: boolean;
  status: string;
  next_opens?: string;
  closes_at?: string;
  minutes_until_close?: number;
}

interface Menu {
  id: string;
  name: string;
  template: string;
  /** Sólo la configuración pública de pedidos. Nunca template_settings completo. */
  ordering?: unknown;
}

interface Category {
  id: string;
  name: string;
  position: number;
  description?: string;
  items: Item[];
}

interface Item {
  id: string;
  name: string;
  description?: string;
  price: number;
  image_url?: string;
  vegetarian: boolean;
  vegan: boolean;
  gluten_free: boolean;
  dairy_free: boolean;
  nut_free: boolean;
  keto_friendly: boolean;
  high_protein: boolean;
  spice_level: number;
  is_available: boolean;
  position: number;
  tags: string[];
  option_groups?: OptionGroup[];
}

interface OptionGroup {
  id: string;
  name: string;
  selection_type: string;
  options: ItemOption[];
}

interface ItemOption {
  id: string;
  name: string;
  price_modifier: number;
  is_available: boolean;
}

interface Promotion {
  id: string;
  title: string;
  description?: string;
  discount_percentage?: number;
  discount_amount?: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  image_url?: string;
}

function readableTextColor(background?: string): string {
  if (!background || !/^#[0-9a-f]{6}$/i.test(background)) return "hsl(var(--primary-foreground))";
  const channels = [1, 3, 5].map((index) => parseInt(background.slice(index, index + 2), 16) / 255);
  const luminance = channels
    .map((channel) => channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4))
    .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index], 0);
  return luminance > 0.48 ? "hsl(20 14% 4%)" : "hsl(0 0% 100%)";
}

/**
 * Contrato público v1 devuelto por public.get_public_menu_payload(slug_param).
 * Se tipa localmente: el RPC no está en src/integrations/supabase/types.ts
 * y ese archivo no debe editarse ni regenerarse.
 */
interface PublicMenuPayload {
  restaurant: {
    id: string;
    name: string;
    logo_url?: string | null;
    brand_primary?: string | null;
    brand_accent?: string | null;
    whatsapp?: string | null;
    instagram?: string | null;
    facebook?: string | null;
    show_plaxter_branding?: boolean | null;
  };
  menu: {
    id: string;
    name: string;
    template: string;
    /**
     * Única fuente pública de la configuración de pedidos.
     * Si el RPC todavía no la expone, los pedidos quedan apagados (default seguro).
     * NUNCA se lee template_settings completo: puede contener notas internas.
     */
    ordering?: unknown;
  };
  categories: Array<{
    id: string;
    name: string;
    description?: string | null;
    position: number;
    items: Array<{
      id: string;
      name: string;
      description?: string | null;
      price: number;
      image_url?: string | null;
      vegetarian?: boolean | null;
      vegan?: boolean | null;
      gluten_free?: boolean | null;
      dairy_free?: boolean | null;
      nut_free?: boolean | null;
      keto_friendly?: boolean | null;
      high_protein?: boolean | null;
      spice_level?: number | null;
      tags?: string[] | null;
      position: number;
      is_available: boolean;
      option_groups?: Array<{
        id: string;
        name: string;
        selection_type: string;
        options: Array<{
          id: string;
          name: string;
          price_modifier: number;
          is_available: boolean;
        }>;
      }> | null;
    }>;
  }>;
  promotions: Promotion[];
  /** Horario público mínimo (día, abierto, apertura, cierre). */
  hours?: Array<{
    day_of_week: number;
    is_open?: boolean | null;
    open_time?: string | null;
    close_time?: string | null;
  }> | null;
  /** Bloque SEO calculado en servidor (indexabilidad incluida). */
  seo?: {
    slug?: string | null;
    meta_title?: string | null;
    meta_description?: string | null;
    meta_image_url?: string | null;
    is_indexable?: boolean | null;
    content_updated_at?: string | null;
  } | null;
}



export default function PublicMenuBySlug() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [menu, setMenu] = useState<Menu | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [restaurantStatus, setRestaurantStatus] = useState<RestaurantStatus | null>(null);
  const [seoPayload, setSeoPayload] = useState<PublicMenuPayload['seo']>(null);
  const [seoHours, setSeoHours] = useState<NonNullable<PublicMenuPayload['hours']>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryChooserOpen, setCategoryChooserOpen] = useState(false);
  const categoryRefs = useRef<Record<string, HTMLElement | null>>({});
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const categoryNavRef = useRef<HTMLDivElement>(null);
  const stickyNavRef = useRef<HTMLElement>(null);
  const isProgrammaticScroll = useRef(false);
  const scrollReleaseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [navHeight, setNavHeight] = useState(64);

  // Pedidos por WhatsApp (default apagado si el menú no lo configuró)
  const cart = useMenuCart();
  const [cartOpen, setCartOpen] = useState(false);
  const orderingSettings = useMemo(
    () => (menu?.ordering ? parseOrderingSettings({ ordering: menu.ordering }) : DEFAULT_ORDERING_SETTINGS),
    [menu?.ordering]
  );
  const whatsappDigits = useMemo(
    () => normalizeWhatsappNumber(restaurant?.whatsapp, orderingSettings.countryCode),
    [restaurant?.whatsapp, orderingSettings.countryCode]
  );

  const orderingActive = isOrderingActive(orderingSettings, whatsappDigits);
  const orderingModes = useMemo(() => enabledModes(orderingSettings), [orderingSettings]);

  // Las etiquetas fijas de index.html (portada) no deben describir este micrositio.
  const categoryNamesKey = categories.map((category) => category.name).join('|');
  useEffect(() => {
    if (!restaurant?.name) return;
    const seoNow = buildRestaurantSeo({
      name: restaurant.name,
      slug: seoPayload?.slug || restaurant.menu_slug || slug || '',
      metaTitle: seoPayload?.meta_title,
      metaDescription: seoPayload?.meta_description,
      metaImageUrl: seoPayload?.meta_image_url,
      logoUrl: restaurant.logo_url,
      isIndexable: seoPayload?.is_indexable,
      categoryNames: categoryNamesKey ? categoryNamesKey.split('|') : [],
      orderingActive,
      whatsappDigits,
      instagram: restaurant.instagram,
      facebook: restaurant.facebook,
      hours: seoHours,
    });
    return applyStaticHeadOverride(document, {
      description: seoNow.description,
      canonical: seoNow.canonical,
      ogTitle: seoNow.title,
      ogDescription: seoNow.description,
      ogImage: seoNow.image,
      ogUrl: seoNow.canonical,
    });
  }, [
    restaurant?.name,
    restaurant?.menu_slug,
    restaurant?.logo_url,
    restaurant?.instagram,
    restaurant?.facebook,
    seoPayload,
    seoHours,
    slug,
    categoryNamesKey,
    orderingActive,
    whatsappDigits,
  ]);

  const openCart = () => {
    trackOrderingEventOnce('cart_open', { lines: cart.lines.length });
    setCartOpen(true);
  };

  
  // PWA Hooks
  const { cacheMenu, isOnline } = usePWA();
  const { cacheCurrentMenu } = useMenuCache(slug, menu);
  const { trackGestureUsed } = usePWAAnalytics();
  
  // Pull to refresh: actualiza en segundo plano, sin vaciar la carta ya visible
  const { pullDistance, isRefreshing, progress, isPulling } = usePullToRefresh({
    onRefresh: async () => {
      const ok = await loadMenuBySlug({ background: true });
      trackGestureUsed('pull-to-refresh');
      if (ok) toast.success('Menú actualizado');
    },
    containerRef
  });


  useEffect(() => {
    if (!slug) {
      setError(true);
      setLoading(false);
      return;
    }

    loadMenuBySlug();
  }, [slug]);

  // Separate useEffect for status updates to avoid multiple intervals
  useEffect(() => {
    if (!restaurant?.id) return;

    // Initial status load
    updateRestaurantStatus();
    
    // Update status every minute
    const statusInterval = setInterval(() => {
      updateRestaurantStatus();
    }, 60000); // 60 segundos

    return () => clearInterval(statusInterval);
  }, [restaurant?.id]);

  const loadMenuBySlug = async ({ background = false }: { background?: boolean } = {}): Promise<boolean> => {
    try {
      if (!background) setLoading(true);

      // Frontera pública v1: una sola llamada de datos para toda la carta.
      // El RPC no existe en types.ts generado -> cast local mínimo.
      const { data, error: payloadError } = await (supabase.rpc as any)(
        'get_public_menu_payload',
        { slug_param: slug }
      );

      if (payloadError || !data) {
        if (!background) setError(true);
        return false;
      }

      const payload = data as PublicMenuPayload;

      if (!payload.restaurant?.id || !payload.menu?.id) {
        if (!background) setError(true);
        return false;
      }


      const restaurantData: Restaurant = {
        id: payload.restaurant.id,
        name: payload.restaurant.name,
        logo_url: payload.restaurant.logo_url ?? undefined,
        brand_primary: payload.restaurant.brand_primary ?? undefined,
        brand_accent: payload.restaurant.brand_accent ?? undefined,
        menu_slug: slug ?? '',
        slug: slug ?? undefined,
        show_plaxter_branding: payload.restaurant.show_plaxter_branding ?? undefined,
        whatsapp: payload.restaurant.whatsapp ?? undefined,
        instagram: payload.restaurant.instagram ?? undefined,
        facebook: payload.restaurant.facebook ?? undefined,
      };

      setRestaurant(restaurantData);

      // Hito de activación: primera visita real a una carta publicada.
      // El servidor valida que la carta exista y esté publicada y guarda una
      // sola fila por restaurante. No se envía ningún dato del visitante.
      // Nunca debe poder impedir que la carta se muestre: va aislado.
      if (!background) {
        try {
          const rpc = supabase.rpc.bind(supabase) as unknown as (
            fn: string,
            args: Record<string, unknown>,
          ) => PromiseLike<unknown>;
          void Promise.resolve(
            rpc('record_first_public_view', { p_restaurant_id: restaurantData.id }),
          ).catch(() => undefined);
        } catch {
          /* la medición nunca bloquea la carta pública */
        }
      }

      setMenu({
        id: payload.menu.id,
        name: payload.menu.name,
        template: payload.menu.template,
        ordering: payload.menu.ordering ?? null,
      });

      // El payload ya llega ordenado, filtrado por disponibilidad y con
      // option_groups anidados: no hay uniones ni fetches adicionales.
      const processedCategories: Category[] = (payload.categories || []).map((category) => ({
        id: category.id,
        name: category.name,
        description: category.description ?? undefined,
        position: category.position,
        items: (category.items || []).map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description ?? undefined,
          price: Number(item.price),
          image_url: item.image_url ?? undefined,
          vegetarian: !!item.vegetarian,
          vegan: !!item.vegan,
          gluten_free: !!item.gluten_free,
          dairy_free: !!item.dairy_free,
          nut_free: !!item.nut_free,
          keto_friendly: !!item.keto_friendly,
          high_protein: !!item.high_protein,
          spice_level: item.spice_level ?? 0,
          is_available: true,
          position: item.position,
          tags: item.tags ?? [],
          option_groups: (item.option_groups || []).map((group) => ({
            id: group.id,
            name: group.name,
            selection_type: group.selection_type,
            options: (group.options || []).map((option) => ({
              id: option.id,
              name: option.name,
              price_modifier: Number(option.price_modifier),
              is_available: true,
            })),
          })),
        })),
      })).filter((category) => category.items.length > 0);

      setCategories(processedCategories);
      setPromotions(payload.promotions || []);
      setSeoPayload(payload.seo ?? null);
      setSeoHours(Array.isArray(payload.hours) ? payload.hours : []);

      // Elegante prioriza una sola categoría para que cartas extensas sean fáciles de recorrer.
      // Lista y Cuadrícula conservan su comportamiento actual de categorías abiertas.
      // En una actualización en segundo plano se conserva el contexto del visitante
      // (categoría abierta/activa) siempre que siga existiendo en la carta.
      const availableIds = new Set(processedCategories.map((category) => category.id));
      const initialCategoryIds = payload.menu.template === 'elegant'
        ? processedCategories.slice(0, 1).map((category) => category.id)
        : processedCategories.map((category) => category.id);

      if (background) {
        setExpandedCategories((prev) => {
          const kept = prev.filter((id) => availableIds.has(id));
          return kept.length > 0 ? kept : initialCategoryIds;
        });
        setActiveCategoryId((prev) =>
          prev && availableIds.has(prev) ? prev : processedCategories[0]?.id ?? null
        );
      } else {
        setExpandedCategories(initialCategoryIds);
        setActiveCategoryId(processedCategories[0]?.id ?? null);
      }

      // Pre-cachear imágenes del menú para acceso offline
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const imageUrls = processedCategories
          .flatMap(cat => cat.items)
          .map(item => item.image_url)
          .filter(Boolean) as string[];

        if (imageUrls.length > 0) {
          navigator.serviceWorker.controller.postMessage({
            type: 'PRECACHE_IMAGES',
            images: imageUrls
          });
        }
      }

      // Estado abierto/cerrado: RPC independiente (también refrescado cada 60 s)
      const { data: statusData } = await supabase.rpc('is_restaurant_open', {
        restaurant_id_param: restaurantData.id
      });

      if (statusData && typeof statusData === 'object') {
        setRestaurantStatus(statusData as unknown as RestaurantStatus);
      }

      // Analytics: fail-silent y sin escrituras extra por actualizar en segundo plano
      if (!background) void trackVisit(payload.menu.id);

      return true;
    } catch (error) {
      // Error inesperado silenciado: en segundo plano se conserva la carta cargada
      if (!background) setError(true);
      return false;
    } finally {
      if (!background) setLoading(false);
    }
  };


  const trackVisit = async (menuId: string) => {
    if (!slug || !menuId) return;

    try {
      // Deduplicación diaria por dispositivo
      const sessionKey = `menu_visit_${slug}_${new Date().toDateString()}`;
      if (localStorage.getItem(sessionKey)) return;

      // Origen normalizado desde la URL (sólo valores permitidos). Se usa el
      // campo existente `referrer` únicamente cuando no hay referrer real.
      const ALLOWED_SOURCES = ['qr'];
      const rawSource = new URLSearchParams(window.location.search).get('src')?.toLowerCase().trim();
      const normalizedSource = rawSource && ALLOWED_SOURCES.includes(rawSource) ? rawSource : null;

      // Registro server-side: valida que el menú exista y esté publicado y
      // aplica un tope antiabuso. No almacena IP ni identificadores nuevos.
      await supabase.rpc('track_menu_visit', {
        p_menu_id: menuId,
        p_referrer: document.referrer || normalizedSource || null,
      });

      localStorage.setItem(sessionKey, 'visited');
    } catch (error) {
      // Error de tracking silenciado - no mostrar al usuario
    }
  };

  const updateRestaurantStatus = async () => {
    if (!restaurant) return;

    try {
      const { data: statusData } = await supabase.rpc('is_restaurant_open', {
        restaurant_id_param: restaurant.id
      });
      
      if (statusData && typeof statusData === 'object') {
        setRestaurantStatus(statusData as unknown as RestaurantStatus);
      }
    } catch (error) {
      // Error de estado silenciado
    }
  };

  // Pedidos por WhatsApp: botón "Agregar" reutilizado por las tres plantillas.
  const renderOrderAction = (item: Item, className = "mt-3") => {
    if (!orderingActive) return null;
    return (
      <div className={`flex justify-end ${className}`}>
        <AddToOrderButton
          item={{
            id: item.id,
            name: item.name,
            price: item.price,
            option_groups: item.option_groups,
          }}
          onAdd={cart.add}
          accentColor={restaurant?.brand_primary || undefined}
        />
      </div>
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  const getSpiceLevel = (level: number) => {
    return Array.from({ length: level }, (_, i) => (
      <Flame key={i} className="w-3 h-3 text-red-500 inline-block" />
    ));
  };

  const isElegantTemplate = menu?.template === 'elegant';

  // En Elegante se mantiene una sola categoría abierta; las otras plantillas
  // conservan su acordeón múltiple. La búsqueda abre temporalmente cada resultado.
  const handleAccordionChange = (value: string[]) => {
    if (isElegantTemplate && !isSearching) {
      const newlyOpened = value.find((categoryId) => !expandedCategories.includes(categoryId));
      const nextCategoryId = newlyOpened ?? value[value.length - 1];
      if (!nextCategoryId) return;
      setExpandedCategories(nextCategoryId ? [nextCategoryId] : []);
      if (nextCategoryId) setActiveCategoryId(nextCategoryId);
      return;
    }

    setExpandedCategories(value);
  };

  // Búsqueda de platillos (cliente): insensible a mayúsculas y acentos
  const normalizeSearchText = (value: string) =>
    value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;

  const visibleCategories = useMemo(() => {
    if (!isSearching) return categories;
    const q = normalizeSearchText(trimmedQuery);
    return categories
      .map((category) => ({
        ...category,
        items: category.items.filter((item) =>
          normalizeSearchText(item.name).includes(q) ||
          normalizeSearchText(item.description || "").includes(q)
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [categories, trimmedQuery, isSearching]);

  const totalMatches = useMemo(
    () => visibleCategories.reduce((sum, cat) => sum + cat.items.length, 0),
    [visibleCategories]
  );

  // Durante la búsqueda todas las categorías con resultados se muestran abiertas
  const effectiveExpandedCategories = isSearching
    ? visibleCategories.map((cat) => cat.id)
    : expandedCategories;

  // Ir a una categoría: la abre si estaba cerrada y hace scroll suave sin ocultar el título
  const scrollToCategory = (categoryId: string) => {
    setActiveCategoryId(categoryId);
    setCategoryChooserOpen(false);
    if (isElegantTemplate && !isSearching) {
      setExpandedCategories([categoryId]);
    } else if (!expandedCategories.includes(categoryId)) {
      setExpandedCategories((prev) => [...prev, categoryId]);
    }
    isProgrammaticScroll.current = true;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // La categoría anterior se cierra con animación, así que el destino se
    // recalcula al terminar: el encabezado siempre queda bajo la barra fija.
    const align = (behavior: ScrollBehavior) => {
      const el = categoryRefs.current[categoryId];
      if (!el) return;
      const top = window.scrollY + el.getBoundingClientRect().top - (navHeight + 12);
      window.scrollTo({ top: Math.max(0, top), behavior });
    };
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        align(reduceMotion ? "auto" : "smooth");
        if (scrollReleaseRef.current) clearTimeout(scrollReleaseRef.current);
        // Corrección instantánea tras cerrar la categoría anterior: no introduce
        // movimiento adicional, por eso también aplica con movimiento reducido.
        scrollReleaseRef.current = setTimeout(() => {
          align("auto");
          isProgrammaticScroll.current = false;
          scrollReleaseRef.current = null;
        }, 380);

      });
    });
  };


  // Mantener visible el chip activo dentro de la barra scrollable
  useEffect(() => {
    if (!activeCategoryId) return;
    const chip = chipRefs.current[activeCategoryId];
    const scroller = categoryNavRef.current;
    if (!chip || !scroller) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const frame = window.requestAnimationFrame(() => {
      const left = chip.offsetLeft - (scroller.clientWidth - chip.offsetWidth) / 2;
      scroller.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? "auto" : "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeCategoryId, isSearching]);

  useEffect(() => () => {
    if (scrollReleaseRef.current) clearTimeout(scrollReleaseRef.current);
  }, []);

  // Altura real de la barra fija: evita desplazamientos con valores inventados
  useEffect(() => {
    const el = stickyNavRef.current;
    if (!el) return;
    const measure = () => setNavHeight(Math.round(el.getBoundingClientRect().height) || 64);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [categories.length, isSearching, isElegantTemplate]);

  const categoryScrollMargin = navHeight + 12;

  // Scroll spy: marca la categoría visible
  useEffect(() => {
    if (categories.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        const id = visible?.target.getAttribute("data-category-id");
        if (id) setActiveCategoryId(id);
      },
      { rootMargin: `-${navHeight + 8}px 0px -70% 0px`, threshold: 0 }
    );
    categories.forEach((cat) => {
      const el = categoryRefs.current[cat.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [categories, navHeight]);

  // Barra sticky de categorías (solo cuando hay más de una)
  const hasCategoryNav = categories.length >= 2;
  const renderCategoryNav = () => {
    if (!hasCategoryNav) return null;
    return (
      <nav
        ref={stickyNavRef}
        aria-label="Categorías del menú"
        className="sticky top-0 z-30 -mx-4 border-b border-border bg-background/95 px-4 py-2 shadow-sm backdrop-blur"
      >

        <div className="flex items-center gap-2">
          {isElegantTemplate && !isSearching && (
            <Sheet open={categoryChooserOpen} onOpenChange={setCategoryChooserOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-11 min-w-11 shrink-0 gap-2 px-3 md:hidden"
                  aria-label="Ver todas las categorías"
                >
                  <ListTree className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden min-[360px]:inline">Categorías</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="max-h-[82dvh] rounded-t-lg px-4">
                <SheetHeader className="pr-10 text-left">
                  <SheetTitle>Categorías</SheetTitle>
                  <SheetDescription>Elige una sección de la carta.</SheetDescription>
                </SheetHeader>
                <div className="mt-5 grid gap-2 pb-2">
                  {categories.map((category) => {
                    const isActive = activeCategoryId === category.id;
                    return (
                      <SheetClose asChild key={category.id}>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => scrollToCategory(category.id)}
                          aria-current={isActive ? "true" : undefined}
                          className={`min-h-[48px] w-full justify-between px-4 text-left ${isActive ? "border-transparent shadow-sm hover:opacity-95" : ""}`}
                          style={isActive ? {
                            backgroundColor: restaurant?.brand_primary || "hsl(var(--primary))",
                            color: readableTextColor(restaurant?.brand_primary),
                          } : undefined}
                        >
                          <span className="min-w-0 truncate">{category.name}</span>
                          <span className={isActive ? "opacity-80" : "text-muted-foreground"}>
                            {category.items.length}
                          </span>
                        </Button>
                      </SheetClose>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          )}
          <div className="relative min-w-0 flex-1">
          <div ref={categoryNavRef} className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-proximity pr-9">
          {visibleCategories.map((category) => {
            const isActive = activeCategoryId === category.id;
            return (
              <button
                key={category.id}
                ref={(el) => {
                  chipRefs.current[category.id] = el;
                }}
                type="button"
                onClick={() => scrollToCategory(category.id)}
                aria-current={isActive ? "true" : undefined}
                className={`snap-start shrink-0 min-h-[44px] px-3.5 rounded-full text-sm font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  isActive
                    ? "text-primary-foreground border-transparent shadow-sm"
                    : "bg-muted/60 text-foreground border-border hover:bg-muted"
                }`}
                style={isActive ? {
                  backgroundColor: restaurant?.brand_primary || "hsl(var(--primary))",
                  color: readableTextColor(restaurant?.brand_primary),
                } : undefined}
              >
                {category.name}
              </button>
            );
          })}
          </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end w-10 bg-gradient-to-l from-background via-background/70 to-transparent"
        >
          <ChevronRight className="h-4 w-4 text-muted-foreground/70 mr-0.5" />
          </div>
        </div>
          {/* WhatsApp persistente en móvil: vive en la barra, nunca tapa el contenido */}
          {restaurant?.whatsapp && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Consultar por WhatsApp con ${restaurant.name}`}
              title={`Consultar por WhatsApp con ${restaurant.name}`}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-green-600 text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:hidden"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
            </a>
          )}
        </div>

      </nav>
    );
  };



  const getDietaryIcons = (item: Item) => {
    const icons = [];
    if (item.vegetarian) icons.push({ icon: Leaf, label: "Vegetariano", color: "text-green-600" });
    if (item.vegan) icons.push({ icon: Leaf, label: "Vegano", color: "text-green-700" });
    if (item.gluten_free) icons.push({ icon: Wheat, label: "Sin Gluten", color: "text-amber-600" });
    if (item.dairy_free) icons.push({ icon: Droplets, label: "Sin Lácteos", color: "text-blue-600" });
    if (item.nut_free) icons.push({ icon: Shield, label: "Sin Nueces", color: "text-orange-600" });
    if (item.keto_friendly) icons.push({ icon: Zap, label: "Keto", color: "text-purple-600" });
    if (item.high_protein) icons.push({ icon: Dumbbell, label: "Alto en Proteína", color: "text-red-600" });
    return icons;
  };

  // Plantilla Lista Vertical con Acordeón
  const renderListTemplate = () => (
    <div className="space-y-6">
      <Accordion 
        type="multiple" 
        value={effectiveExpandedCategories} 
        onValueChange={handleAccordionChange}
        className="space-y-4"
      >
        {visibleCategories.map((category) => (
          <AccordionItem 
            key={category.id} 
            value={category.id}
            data-category-id={category.id}
            ref={(el) => { categoryRefs.current[category.id] = el; }}
            className="border-0"
            style={{ scrollMarginTop: categoryScrollMargin }}

          >
            <Card className="overflow-hidden bg-card dark:bg-card border border-border">
              <AccordionTrigger 
                className="hover:no-underline p-0 [&[data-state=open]>div>svg]:rotate-180 [&>svg]:hidden"
              >
                 <div 
                  className="flex items-center justify-between w-full p-4 text-white font-semibold text-lg rounded-lg"
                  style={{
                    backgroundColor: restaurant?.brand_primary || '#3B82F6'
                  }}
                >
                  <div className="flex flex-col items-start">
                    <span>{category.name}</span>
                    {category.description && (
                      <span className="text-sm text-white/80 font-normal mt-1">
                        {category.description}
                      </span>
                    )}
                  </div>
                  <ChevronDown className="h-5 w-5 text-white/80 transition-transform duration-200 flex-shrink-0 ml-2" />
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0">
                <CardContent className="p-0">
                  <div className="space-y-1">
                    {category.items.map((item, index) => (
                      <div 
                        key={item.id} 
                        className={`p-4 hover:bg-muted/50 dark:hover:bg-muted/30 transition-colors animate-fade-in touch-scale ${index > 0 ? 'border-t border-border' : ''}`}
                      >
                        <div className="flex gap-4">
                          {item.image_url && (
                            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-muted aspect-square">
                              <img 
                                src={item.image_url} 
                                alt={`Foto de ${item.name}`}
                                width={160}
                                height={160}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="text-lg font-semibold text-foreground dark:text-foreground pr-2 break-words">
                                {item.name}
                              </h3>
                              <div className="text-lg font-bold text-primary flex-shrink-0 whitespace-nowrap">
                                {item.option_groups && item.option_groups.length > 0 ? (
                                  <span className="text-sm">Desde {formatPrice(item.price)}</span>
                                ) : (
                                  formatPrice(item.price)
                                )}
                              </div>
                            </div>
                            
                            {item.description && (
                              <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                                {item.description}
                              </p>
                            )}
                            
              <div className="flex flex-wrap gap-2 items-center">
                {getDietaryIcons(item).map((iconData, idx) => {
                  const IconComponent = iconData.icon;
                  return (
                    <Badge key={idx} variant="secondary" className="text-xs flex items-center gap-1">
                      <IconComponent className={`h-3 w-3 ${iconData.color}`} />
                      {iconData.label}
                    </Badge>
                  );
                })}
                
                {item.spice_level > 0 && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    <div className="flex">{getSpiceLevel(item.spice_level)}</div>
                    <span className="ml-1">
                      {item.spice_level === 1 && 'Poco picante'}
                      {item.spice_level === 2 && 'Picante'}
                      {item.spice_level === 3 && 'Muy picante'}
                    </span>
                  </Badge>
                )}
              </div>
                          </div>
                          
                          {/* Option Groups */}
                          {item.option_groups && item.option_groups.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {item.option_groups.map((group) => (
                                <div key={group.id} className="text-sm">
                                  <p className="font-medium text-muted-foreground mb-1">
                                    {group.name}
                                    <span className="ml-1 font-normal text-xs">
                                      {group.selection_type === 'single_required' ? '(elige una opción)' : '(opcional)'}
                                    </span>
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {group.options.map((option) => (
                                      <Badge 
                                        key={option.id} 
                                        variant="outline" 
                                        className="text-xs font-normal"
                                      >
                                        {option.name}
                                        {option.price_modifier > 0 && (
                                          <span className="ml-1 text-primary">+{formatPrice(option.price_modifier)}</span>
                                        )}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                           )}
                           {renderOrderAction(item)}
                         </div>
                       </div>
                     ))}
                  </div>
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );

  // Plantilla Cuadrícula con Acordeón
  const renderGridTemplate = () => (
    <div className="space-y-6">
      <Accordion 
        type="multiple" 
        value={effectiveExpandedCategories} 
        onValueChange={handleAccordionChange}
        className="space-y-6"
      >
        {visibleCategories.map((category) => (
          <AccordionItem 
            key={category.id} 
            value={category.id}
            data-category-id={category.id}
            ref={(el) => { categoryRefs.current[category.id] = el; }}
            className="border-0"
            style={{ scrollMarginTop: categoryScrollMargin }}

          >
            <div className="space-y-4">
              <AccordionTrigger 
                className="hover:no-underline p-0 [&[data-state=open]>div>svg]:rotate-180 [&>svg]:hidden"
              >
                <div
                  className="flex items-center justify-between w-full text-2xl font-bold text-white p-4 rounded-lg"
                  style={{
                    backgroundColor: restaurant?.brand_primary || '#3B82F6'
                  }}
                >
                  <div className="flex flex-col items-start">
                    <span>{category.name}</span>
                    {category.description && (
                      <span className="text-sm text-white/80 font-normal mt-1">
                        {category.description}
                      </span>
                    )}
                  </div>
                  <ChevronDown className="h-6 w-6 text-white/80 transition-transform duration-200 flex-shrink-0 ml-2" />
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 pt-4 animate-fade-in">
                  {category.items.map((item) => (
                    <Card key={item.id} className="overflow-hidden bg-card dark:bg-card shadow-md hover:shadow-lg transition-shadow hover-scale">
                      {item.image_url && (
                        <div className="aspect-square relative bg-muted">
                          <img 
                            src={item.image_url} 
                            alt={`Foto de ${item.name}`}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-bold text-lg text-foreground dark:text-foreground leading-tight break-words min-w-0">{item.name}</h3>
                          <span className="font-bold text-xl flex-shrink-0 text-primary whitespace-nowrap">
                            {item.option_groups && item.option_groups.length > 0 ? (
                              <span className="text-sm">Desde {formatPrice(item.price)}</span>
                            ) : (
                              formatPrice(item.price)
                            )}
                          </span>
                        </div>
                        
                        {item.description && (
                          <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                        
                        <div className="flex flex-wrap gap-2">
                          {getDietaryIcons(item).map((icon, index) => (
                            <Badge key={index} variant="secondary" className="text-xs flex items-center gap-1">
                              <icon.icon className={`w-3 h-3 ${icon.color}`} />
                              <span>{icon.label}</span>
                            </Badge>
                          ))}
                          
                {item.spice_level > 0 && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    <div className="flex">{getSpiceLevel(item.spice_level)}</div>
                  </Badge>
                )}
                          
                          {item.tags.length > 0 && (
                            item.tags.map((tag, tagIndex) => (
                              <Badge key={tagIndex} variant="outline" className="text-xs border-border text-muted-foreground dark:text-muted-foreground">
                                {tag}
                              </Badge>
                            ))
                          )}
                        </div>
                        
                        {/* Option Groups */}
                        {item.option_groups && item.option_groups.length > 0 && (
                          <div className="mt-3 space-y-2 border-t pt-2">
                            {item.option_groups.map((group) => (
                              <div key={group.id} className="text-sm">
                                <p className="font-medium text-muted-foreground mb-1">
                                  {group.name}
                                  <span className="ml-1 font-normal text-xs">
                                    {group.selection_type === 'single_required' ? '(elige una opción)' : '(opcional)'}
                                  </span>
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {group.options.map((option) => (
                                    <Badge key={option.id} variant="outline" className="text-xs font-normal">
                                      {option.name}
                                      {option.price_modifier > 0 && (
                                        <span className="ml-1 text-primary">+{formatPrice(option.price_modifier)}</span>
                                      )}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {renderOrderAction(item)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </AccordionContent>
            </div>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );

  // Plantilla Elegante con Acordeón
  const renderElegantTemplate = () => (
    <div className="mx-auto max-w-5xl motion-safe:animate-fade-in">
      <Accordion 
        type="multiple" 
        value={effectiveExpandedCategories} 
        onValueChange={handleAccordionChange}
        className="space-y-2.5 sm:space-y-4"
      >
        {visibleCategories.map((category, categoryIndex) => (
          <AccordionItem 
            key={category.id} 
            value={category.id}
            data-category-id={category.id}
            ref={(el) => { categoryRefs.current[category.id] = el; }}
            className="overflow-hidden rounded-lg border border-border/70 bg-card/60 data-[state=open]:border-primary/25 data-[state=open]:bg-card data-[state=open]:shadow-elegant motion-safe:transition-[background-color,border-color,box-shadow] motion-safe:duration-300"
            style={{ scrollMarginTop: categoryScrollMargin }}

          >
            <div>
              <AccordionTrigger 
                className="group min-h-[68px] px-4 py-3 hover:no-underline sm:px-5 [&>svg]:hidden"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
                  <span
                    aria-hidden="true"
                    className="h-9 w-1 shrink-0 rounded-full opacity-60 motion-safe:transition-opacity group-data-[state=open]:opacity-100"
                    style={{ backgroundColor: restaurant?.brand_accent || restaurant?.brand_primary || 'hsl(var(--primary))' }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-baseline justify-between gap-3">
                      <h2
                        className="min-w-0 break-words text-lg font-semibold leading-tight sm:text-xl"
                        style={{ color: restaurant?.brand_primary || 'hsl(var(--primary))' }}
                      >
                        {category.name}
                      </h2>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {category.items.length} {category.items.length === 1 ? 'platillo' : 'platillos'}
                      </span>
                    </div>
                    {category.description && (
                      <p className="mt-1 line-clamp-1 text-sm font-normal text-muted-foreground">
                        {category.description}
                      </p>
                    )}
                  </div>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted/70 motion-safe:transition-colors group-hover:bg-muted">
                    <Plus
                      aria-hidden="true"
                      className="h-5 w-5 motion-safe:transition-transform motion-safe:duration-300 group-data-[state=open]:rotate-45"
                      style={{ color: restaurant?.brand_primary || 'hsl(var(--primary))' }}
                    />
                  </span>
                </div>
              </AccordionTrigger>
              
              <AccordionContent className="pb-0">
                <div className="border-t border-border/70 px-3 pb-3 sm:px-5 sm:pb-5">
                  {category.items.map((item, itemIndex) => (
                    <article key={item.id} className="group border-b border-border/60 py-4 last:border-b-0 motion-safe:transition-colors sm:py-5 sm:hover:bg-muted/25">
                      <div className="flex items-start gap-3 sm:gap-5">
                        {item.image_url && (
                          <div className="aspect-square w-24 xs:w-28 sm:w-32 flex-shrink-0 overflow-hidden rounded-md bg-muted">
                            <img 
                              src={item.image_url} 
                              alt={`Foto de ${item.name}`}
                              width={256}
                              height={256}
                              loading={categoryIndex === 0 && itemIndex === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                              sizes="(max-width: 475px) 96px, (max-width: 640px) 112px, 128px"
                              className="h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover:scale-[1.03]"
                            />
                          </div>
                        )}
                        
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex flex-col gap-1 xs:flex-row xs:items-start xs:justify-between xs:gap-3">
                            <h3 className="min-w-0 text-base sm:text-lg font-semibold leading-snug text-foreground break-words">
                              {item.name}
                            </h3>
                            <span 
                              className="shrink-0 whitespace-nowrap text-base sm:text-lg font-semibold tabular-nums"
                              style={{ color: restaurant?.brand_primary || 'hsl(var(--primary))' }}
                            >
                              {item.option_groups && item.option_groups.length > 0 ? (
                                <span className="mr-1 text-xs font-normal text-muted-foreground">Desde</span>
                              ) : null}
                              {formatPrice(item.price)}
                            </span>
                          </div>
                          
                          {item.description && (
                            <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground sm:line-clamp-none">
                              {item.description}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {getDietaryIcons(item).map((icon, index) => (
                              <Badge key={index} variant="secondary" className="text-xs flex items-center gap-1">
                                <icon.icon className={`w-3.5 h-3.5 ${icon.color}`} />
                                <span>{icon.label}</span>
                              </Badge>
                            ))}
                            
                {item.spice_level > 0 && (
                  <Badge variant="secondary" className="text-xs flex items-center gap-1">
                    <div className="flex">{getSpiceLevel(item.spice_level)}</div>
                  </Badge>
                )}
                            
                            {item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {item.tags.map((tag, tagIndex) => (
                                  <span key={tagIndex} className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full border border-border">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Option groups for elegant template */}
                          {item.option_groups && item.option_groups.length > 0 && (
                            <div className="mt-3 space-y-3 border-t border-border/60 pt-3">
                              {item.option_groups.map((group) => (
                                <div key={group.id} className="space-y-1">
                                  <p className="text-sm font-medium text-foreground/80">
                                    {group.name}
                                    <span className="ml-1 font-normal text-xs text-muted-foreground">
                                      {group.selection_type === 'single_required' ? '(elige una opción)' : '(opcional)'}
                                    </span>
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {group.options.filter(o => o.is_available).map((option) => (
                                      <Badge key={option.id} variant="outline" className="text-xs font-normal">
                                        {option.name}
                                        {option.price_modifier > 0 && (
                                          <span className="ml-1 text-muted-foreground">+{formatPrice(option.price_modifier)}</span>
                                        )}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                           )}
                           {renderOrderAction(item)}
                         </div>
                       </div>
                     </article>
                  ))}
                </div>
              </AccordionContent>
            </div>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );

  // 🚫 SI SUSCRIPCIÓN VENCIDA, MOSTRAR PANTALLA DE BLOQUEO
  if (loading) {
    return (
      <div className="min-h-screen bg-background dark:bg-background">
        {/* Cabecera de carga con la misma altura que la real: evita saltos de layout */}
        <div className="h-[128px] bg-muted/60 animate-pulse" />
        <div className="container mx-auto p-4 space-y-4">
          <div className="flex gap-2 overflow-hidden">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-10 w-24 rounded-full flex-shrink-0" />
            ))}
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex gap-4 p-4 rounded-lg border border-border">
                <Skeleton className="h-20 w-20 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/3" />
                </div>
              </div>
            ))}
          </div>
          <p className="sr-only">Cargando el menú…</p>
        </div>
      </div>

    );
  }

  if (error || !restaurant || !menu) {
    return (
      <div className="min-h-screen bg-background dark:bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <div className="text-6xl mb-4"><UtensilsCrossed className="w-16 h-16 mx-auto text-muted-foreground" /></div>
            <h1 className="text-2xl font-bold">Menú no encontrado</h1>
            <p className="text-muted-foreground">
              Lo sentimos, no pudimos encontrar el menú que buscas.
            </p>
            <Button onClick={() => navigate('/')} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Etiquetas construidas SÓLO con datos reales del restaurante.
  const seo = buildRestaurantSeo({
    name: restaurant.name,
    slug: seoPayload?.slug || restaurant.menu_slug || slug || '',
    metaTitle: seoPayload?.meta_title,
    metaDescription: seoPayload?.meta_description,
    metaImageUrl: seoPayload?.meta_image_url,
    logoUrl: restaurant.logo_url,
    isIndexable: seoPayload?.is_indexable,
    categoryNames: categories.map((category) => category.name),
    orderingActive,
    whatsappDigits,
    instagram: restaurant.instagram,
    facebook: restaurant.facebook,
    hours: seoHours,
  });
  const menuTitle = seo.title;
  const menuDescription = seo.description;
  const menuImage = seo.image;
  const menuUrl = seo.canonical;

  // CTA de WhatsApp: es una consulta directa con el restaurante, no un checkout dentro de Plaxter
  const whatsappBase = restaurant.whatsapp || '';
  const whatsappMessage = `Hola, vi el menú de ${restaurant.name} y quiero hacer una consulta.`;
  const whatsappHref = `${whatsappBase}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div ref={containerRef} className="min-h-screen bg-background dark:bg-background">
      {/* SEO Meta Tags Dinámicos */}
      <Helmet>
        <title>{menuTitle}</title>
        <meta name="description" content={menuDescription} />
        
        {/* Open Graph para redes sociales */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={menuTitle} />
        <meta property="og:description" content={menuDescription} />
        <meta property="og:image" content={menuImage} />
        <meta property="og:url" content={menuUrl} />
        <meta property="og:site_name" content={restaurant.name} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={menuTitle} />
        <meta name="twitter:description" content={menuDescription} />
        <meta name="twitter:image" content={menuImage} />
        
        {/* Indexación: sólo si el servidor lo autoriza */}
        <meta name="robots" content={seo.robots} />

        {/* Canonical URL */}
        <link rel="canonical" href={menuUrl} />

        {/* Datos estructurados Restaurant, sólo con información comprobada */}
        <script type="application/ld+json">{seo.jsonLdString}</script>
      </Helmet>

      {/* Pull to refresh indicator */}
      <PullToRefreshIndicator 
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        progress={progress}
      />
      
      {/* Header del restaurante */}
      <header 
        className="relative overflow-hidden motion-safe:animate-fade-in"
        style={{
          background: restaurant.brand_primary 
            ? `linear-gradient(135deg, ${restaurant.brand_primary}, ${restaurant.brand_accent || restaurant.brand_primary})`
            : 'linear-gradient(135deg, #3B82F6, #1D4ED8)'
        }}
      >
        <div aria-hidden="true" className="absolute inset-0 bg-background/15" />
        <div className="container relative mx-auto px-4 py-4 sm:px-6 sm:py-7">
          <div className="flex items-start gap-3 sm:items-center sm:gap-5">
            {restaurant.logo_url && (
              <RestaurantLogo src={restaurant.logo_url} name={restaurant.name} size="lg" variant="circle" />
            )}
            <div className="flex-1 min-w-0">
              <p className="mb-1 text-xs font-medium uppercase opacity-80" style={{ color: readableTextColor(restaurant.brand_primary) }}>Carta digital</p>
              <h1 className="text-2xl font-semibold leading-tight sm:text-4xl" style={{ color: readableTextColor(restaurant.brand_primary) }}>{restaurant.name}</h1>
              {seoPayload?.meta_description && (
                <p
                  className="mt-1.5 max-w-2xl text-sm leading-snug opacity-90 sm:text-base"
                  style={{ color: readableTextColor(restaurant.brand_primary) }}
                >
                  {seoPayload.meta_description}
                </p>
              )}
               <div className="mt-2 flex flex-wrap items-center gap-2 sm:mt-3 sm:gap-3">
                {restaurantStatus && (
                  <span className="inline-flex min-h-[32px] w-fit items-center gap-1.5 rounded-full border border-background/35 bg-background/85 px-3 py-1 text-xs font-medium text-foreground shadow-sm">
                    <Clock className="w-3 h-3" />
                    {restaurantStatus.status}
                    {restaurantStatus.next_opens && (
                      <span className="ml-1">• {restaurantStatus.next_opens}</span>
                    )}
                  </span>
                )}
                {/* Redes sociales del restaurante */}
                <RestaurantSocialLinks
                  facebook={restaurant.facebook}
                  instagram={restaurant.instagram}
                  whatsapp={restaurant.whatsapp}
                  name={restaurant.name}
                   className="sm:mt-1"
                  variant="header"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Contenido del menú */}
      <main className={`container mx-auto p-4 space-y-6 ${restaurant.whatsapp ? `${hasCategoryNav ? 'pb-[calc(2rem+env(safe-area-inset-bottom))]' : 'pb-[calc(5rem+env(safe-area-inset-bottom))]'} md:pb-[calc(7rem+env(safe-area-inset-bottom))]` : ''}`}>
        {/* Sección de Promociones */}
        {promotions.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold text-foreground dark:text-foreground">
              Promociones de hoy
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {promotions.map((promotion) => (
                <PromotionCountdown key={promotion.id} promotion={promotion} />
              ))}
            </div>
          </div>
        )}

        {categories.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-4xl mb-4"><UtensilsCrossed className="w-12 h-12 mx-auto text-muted-foreground" /></div>
              <h2 className="text-xl font-semibold mb-2">Menú en preparación</h2>
              <p className="text-muted-foreground">
                Este restaurante está preparando su menú. ¡Vuelve pronto!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6 motion-safe:animate-fade-in">
            {/* Búsqueda de platillos */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" aria-hidden="true" />
              <input
                type="text"
                inputMode="search"
                autoComplete="off"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar platillo…"
                aria-label="Buscar platillo por nombre o descripción"
                className="w-full min-h-[44px] rounded-full border border-border bg-card pl-10 pr-12 py-2 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              {isSearching && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Limpiar búsqueda"
                  className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>

            {isSearching && totalMatches === 0 ? (
              <Card>
                <CardContent className="p-8 text-center space-y-3">
                  <Search className="w-10 h-10 mx-auto text-muted-foreground" aria-hidden="true" />
                  <p className="text-lg font-semibold text-foreground">Sin resultados para “{trimmedQuery}”</p>
                  <p className="text-sm text-muted-foreground">Revisa la escritura o prueba con otro platillo.</p>
                  <Button variant="outline" onClick={() => setSearchQuery("")} className="min-h-[44px]">
                    Limpiar búsqueda
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
            {/* Navegación rápida de categorías */}
            {renderCategoryNav()}

            {/* Renderizar según la plantilla seleccionada */}
            {menu?.template === 'grid' && renderGridTemplate()}
            {menu?.template === 'elegant' && renderElegantTemplate()}
            {(!menu?.template || menu?.template === 'list') && renderListTemplate()}
              </>
            )}
          </div>
        )}

        {/* WhatsApp flotante: en móvil sólo cuando no existe la barra persistente */}
        {restaurant.whatsapp && (
          <div
            className={`fixed right-4 z-40 ${hasCategoryNav || (orderingActive && cart.count > 0) ? 'hidden md:block' : ''}`}


            style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
          >
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Consultar por WhatsApp con ${restaurant.name}`}
              title={`Consultar por WhatsApp con ${restaurant.name}`}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-green-600 text-white shadow-lg transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-auto md:w-auto md:min-h-[48px] md:gap-2 md:px-4 md:py-3"
            >
              <MessageCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              <span className="hidden text-sm font-medium md:inline">Consultar por WhatsApp</span>
            </a>
          </div>
        )}

        {/* Barra de pedido: sólo si el restaurante activó pedidos y hay platillos */}
        {orderingActive && cart.count > 0 && (
          <div
            className="fixed inset-x-0 z-50 border-t border-border bg-background/95 px-4 py-3 shadow-lg backdrop-blur"
            style={{ bottom: 0, paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          >
            <Button
              type="button"
              onClick={openCart}
              className="min-h-[52px] w-full justify-between bg-green-600 text-white hover:bg-green-700"
            >
              <span className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                Ver pedido ({cart.count})
              </span>
              <span className="tabular-nums">
                {cartHasUnknownPrices(cart.lines) && cart.total === 0 ? 'Ver total' : formatPrice(cart.total)}
              </span>
            </Button>
          </div>
        )}

        {orderingActive && whatsappDigits && (
          <OrderCartSheet
            open={cartOpen}
            onOpenChange={setCartOpen}
            restaurantName={restaurant.name}
            whatsappDigits={whatsappDigits}
            menuUrl={menuUrl}
            modes={orderingModes}
            lines={cart.lines}
            total={cart.total}
            onSetQuantity={cart.setQuantity}
            onRemove={cart.remove}
          />
        )}




        {/* Firma Plaxter: solo en plan Free (show_plaxter_branding) */}
        {restaurant.show_plaxter_branding === true && (
          <div className="text-center py-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full border border-blue-200/50 group hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-blue-500 animate-pulse" />
                <ChefHat className="w-4 h-4 text-orange-500 animate-bounce" />
                <Zap className="w-4 h-4 text-yellow-500 animate-pulse" />
              </div>
            
              <div className="text-sm">
                <span className="text-muted-foreground">Crea tu menú con </span>
                <a 
                  href="https://plaxter.app/" 
                  target="about:blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    // Safari iOS compatible approach
                    try {
                      const link = document.createElement('a');
                      link.href = 'https://plaxter.app/';
                      link.target = 'about:blank';
                      link.rel = 'noopener noreferrer';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    } catch (error) {
                      // Fallback for older browsers
                      window.open('https://plaxter.app/', 'about:blank', 'noopener');
                    }
                  }}
                  className="plaxter-wordmark bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 inline-flex items-center gap-1 cursor-pointer"
                  style={{ 
                    textShadow: restaurant.brand_primary ? `0 0 20px ${restaurant.brand_primary}20` : undefined 
                  }}
                >
                  Plaxter
                  <Rocket className="w-3 h-3 text-blue-500 ml-1" />
                </a>
              </div>
            
              <div className="w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
            </div>
          
            <p className="text-xs text-muted-foreground mt-2 opacity-70">
              Menús digitales en segundos
            </p>
          </div>
        )}
      </main>
    </div>
  );
}